import supabase from '@/utils/supabase'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { projectBroadcastPayloadSchema } from '@/schemas/project'

/**
 * useProjectRealtime
 *
 * Subscribes to a private Supabase realtime channel and shows toast notifications
 * for project changes.
 *
 * @param {{
 *   enabled?: boolean,
 *   supabaseJwt?: string | null,
 * }} [opts]
 */
export function useProjectRealtime({ enabled = true, supabaseJwt = null } = {}) {
  /** @type {import('@supabase/supabase-js').RealtimeChannel | null} */
  const channelRef = useRef(null)

  useEffect(() => {
    if (!enabled || !supabaseJwt) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
      return
    }

    supabase.realtime.setAuth(supabaseJwt)

    if (channelRef.current) {
      supabase.removeChannel(channelRef.current)
      channelRef.current = null
    }

    const channel = supabase.channel('project:changes', {
      config: {
        broadcast: { self: false },
        private: true,
      },
    })

    /**
     * Handle project broadcasts.
     *
     * @param {unknown} raw
     */
    const handleProjectBroadcast = (raw) => {
      const parsed = projectBroadcastPayloadSchema.safeParse(raw)
      if (!parsed.success) {
        console.error('Invalid project broadcast payload', parsed.error, raw)
        return
      }

      const eventObj = parsed.data
      const { event } = eventObj
      const inner = eventObj.payload
      const current = inner.record
      const previous = inner.old_record

      switch (event) {
        case 'INSERT': {
          if (!current) break
          toast.success('Project Created', {
            description: current.topic
              ? `"${current.topic}" has been created`
              : 'A new project has been created',
          })
          break
        }

        case 'UPDATE': {
          if (!current) break

          const wasDeleted =
            previous && previous.deleted_at == null && current.deleted_at != null

          const wasArchived =
            previous &&
            previous.archived_at == null &&
            current.archived_at != null

          const wasUnarchived =
            previous &&
            previous.archived_at != null &&
            current.archived_at == null

          if (wasDeleted) {
            toast('Project Deleted', {
              description: current.topic
                ? `"${current.topic}" has been deleted`
                : 'A project has been deleted',
            })
            break
          }

          if (wasArchived) {
            toast('Project Archived', {
              description: current.topic
                ? `"${current.topic}" has been archived`
                : 'A project has been archived',
            })
            break
          }

          if (wasUnarchived) {
            toast('Project Unarchived', {
              description: current.topic
                ? `"${current.topic}" has been restored`
                : 'A project has been restored',
            })
            break
          }

          toast.success('Project Updated', {
            description: current.topic
              ? `"${current.topic}" has been updated`
              : 'A project has been updated',
          })
          break
        }

        default:
          break
      }
    }

    channelRef.current = channel

    channel
      .on('broadcast', { event: 'INSERT' }, handleProjectBroadcast)
      .on('broadcast', { event: 'UPDATE' }, handleProjectBroadcast)
      .subscribe()

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
    }
  }, [enabled, supabaseJwt])
}
